import { useMutation } from '@tanstack/react-query';

import { logout, signOutEverywhereSelf } from './api';
import { noteSignedOut } from './bounce';
import { LOGIN_PATH } from './surfaces';

/**
 * The one way out.
 *
 * It leaves via a FULL page load rather than a client-side route change, deliberately: in an
 * identity controller the strongest guarantee that nothing of the previous person survives —
 * no cached user list, no grant graph, no in-memory session state — is to throw the whole
 * document away. It also records that this exit was deliberate, so the sign-in page says
 * "you've been signed out" rather than reporting a refusal that did not happen (R4). That
 * note rides sessionStorage, which survives the reload.
 */
export function useLogout() {
  return useMutation({
    mutationFn: logout,
    onSettled: () => {
      noteSignedOut();
      window.location.assign(LOGIN_PATH);
    },
  });
}

/**
 * The other way out: end every session for this person, including this one.
 *
 * A SIBLING of `useLogout` rather than a flag inside it. They leave the same way and for the
 * same reason — the whole document is thrown away — but they are two different instructions
 * to the server, and a boolean branching between them would put "end all my sessions" one
 * mistyped argument away from the ordinary exit.
 *
 * `onSettled`, not `onSuccess`: the server clears the cookie on its own response, so once
 * this call has been made the credential is gone whatever the client saw. Staying on a
 * rendered screen holding a dead cookie is the failure this avoids.
 */
export function useSignOutEverywhere() {
  return useMutation({
    mutationFn: signOutEverywhereSelf,
    onSettled: () => {
      noteSignedOut();
      window.location.assign(LOGIN_PATH);
    },
  });
}
